import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, ScanLine, AlertTriangle, CheckCircle, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OmrMode() {
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState("");
  const [uploading, setUploading] = useState(false);
  const [omrResult, setOmrResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/tests`).then(r => setTests(r.data)).catch(() => {});
  }, []);

  const downloadOmrTemplate = () => {
    if (!selectedTest) { toast.error("Select a test first"); return; }
    const test = tests.find(t => t.id === selectedTest);
    if (!test) return;
    window.open(`${API}/omr/template-pdf?question_count=${test.question_count}&test_code=${test.test_code}&booklet_code=${test.booklet_code}`, "_blank");
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTest) return;

    setUploading(true);
    setOmrResult(null);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/omr/upload?test_id=${selectedTest}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOmrResult(res.data.omr_result);
      toast.success("OMR sheet analyzed successfully!");
    } catch (err) {
      toast.error("Failed to process OMR. Please try again.");
    }
    setUploading(false);
  };

  const submitOmrAnswers = async () => {
    if (!omrResult || !selectedTest) return;
    setSubmitting(true);

    const answers = {};
    const omrAnswers = omrResult.answers || {};
    Object.entries(omrAnswers).forEach(([qNum, data]) => {
      const test = tests.find(t => t.id === selectedTest);
      if (test && test.question_ids && parseInt(qNum) <= test.question_ids.length) {
        const qId = test.question_ids[parseInt(qNum) - 1];
        if (data.selected) {
          answers[qId] = { selected: data.selected, confidence: "sure" };
        }
      }
    });

    try {
      await axios.post(`${API}/tests/${selectedTest}/submit`, { answers, time_taken_seconds: null });
      toast.success("OMR test submitted!");
      navigate(`/test/${selectedTest}/results`);
    } catch (err) {
      toast.error("Submission failed");
    }
    setSubmitting(false);
  };

  const selectedTestData = tests.find(t => t.id === selectedTest);

  return (
    <div className="p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="omr-mode-page">
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">OMR Mode</h1>
        <p className="text-muted-foreground mt-2 text-base">
          Practice with physical OMR sheets - just like the real UPSC exam
        </p>
      </div>

      {/* Instructions */}
      <Card className="bg-card border-border border-l-4 border-l-amber-500">
        <CardContent className="p-5">
          <h3 className="font-heading text-sm font-semibold mb-3 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-400" /> How to use OMR Mode
          </h3>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
            <li>First, <strong className="text-foreground">generate a test</strong> from the New Test page</li>
            <li>Select the test below and <strong className="text-foreground">download the OMR template</strong></li>
            <li><strong className="text-foreground">Print the template</strong> and download the test paper PDF</li>
            <li>Fill the OMR sheet with a <strong className="text-foreground">black/blue pen</strong> - darken bubbles completely</li>
            <li><strong className="text-foreground">Scan or photograph</strong> the filled OMR sheet clearly</li>
            <li><strong className="text-foreground">Upload the image</strong> for AI-powered analysis</li>
          </ol>
        </CardContent>
      </Card>

      {/* Test Selection */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Select Test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={selectedTest} onValueChange={setSelectedTest}>
            <SelectTrigger data-testid="omr-test-select" className="w-full">
              <SelectValue placeholder="Choose a test..." />
            </SelectTrigger>
            <SelectContent>
              {tests.map(t => (
                <SelectItem key={t.id} value={t.id}>
                  {t.test_code} - {t.question_count} questions ({t.booklet_code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedTestData && (
            <div className="flex gap-3">
              <Button data-testid="download-omr-template-btn" variant="outline" onClick={downloadOmrTemplate} className="gap-2">
                <Download size={16} /> Download OMR Sheet
              </Button>
              <Button
                data-testid="download-test-paper-btn"
                variant="outline"
                onClick={() => window.open(`${API}/tests/${selectedTest}/paper-pdf`, "_blank")}
                className="gap-2"
              >
                <FileText size={16} /> Download Paper
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Section */}
      {selectedTest && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Upload Filled OMR</CardTitle>
          </CardHeader>
          <CardContent>
            <label
              data-testid="omr-upload-zone"
              className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-border rounded-md cursor-pointer hover:border-primary/50 transition-colors"
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 size={32} className="animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Processing OMR sheet with AI vision...</p>
                </div>
              ) : (
                <>
                  <ScanLine size={32} className="text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Click to upload OMR image</p>
                  <p className="text-xs text-muted-foreground mt-1">JPG, PNG - clear scan recommended</p>
                </>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          </CardContent>
        </Card>
      )}

      {/* OMR Results */}
      {omrResult && (
        <Card className="bg-card border-border" data-testid="omr-results">
          <CardHeader>
            <CardTitle className="font-heading text-lg">OMR Analysis Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Info */}
            <div className="flex gap-4 text-sm">
              <span>Test Code: <strong>{omrResult.test_code || "N/A"}</strong></span>
              <span>Booklet: <strong>{omrResult.booklet_code || "N/A"}</strong></span>
              <span>Roll No: <strong>{omrResult.roll_number || "N/A"}</strong></span>
            </div>

            {/* Flags */}
            {(omrResult.flags || []).length > 0 && (
              <div className="p-3 rounded-sm bg-amber-500/10 border border-amber-500/30">
                <p className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                  <AlertTriangle size={14} /> Potential Issues Detected
                </p>
                {omrResult.flags.map((f, i) => (
                  <p key={i} className="text-xs text-amber-300/80 ml-5">
                    Q.{f.question}: {f.description}
                  </p>
                ))}
              </div>
            )}

            {/* Machine Rejections */}
            {(omrResult.machine_rejections || []).length > 0 && (
              <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/30">
                <p className="text-sm font-semibold text-red-400 mb-2">Machine Rejections</p>
                {omrResult.machine_rejections.map((r, i) => (
                  <p key={i} className="text-xs text-red-300/80 ml-5">
                    Q.{r.question}: {r.reason}
                  </p>
                ))}
              </div>
            )}

            {/* Extracted Answers Preview */}
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mt-4">
              {Object.entries(omrResult.answers || {}).slice(0, 50).map(([num, data]) => (
                <div key={num} className={`text-center p-1.5 rounded-sm border text-xs font-mono ${
                  data.confidence === "clear" ? "border-emerald-500/30 bg-emerald-500/5" :
                  data.confidence === "partial" ? "border-amber-500/30 bg-amber-500/5" :
                  "border-red-500/30 bg-red-500/5"
                }`}>
                  <span className="text-muted-foreground">{num}</span>
                  <br />
                  <span className="font-bold">{data.selected || "-"}</span>
                </div>
              ))}
            </div>

            <Button
              data-testid="submit-omr-btn"
              onClick={submitOmrAnswers}
              disabled={submitting}
              className="bg-primary hover:bg-primary/90 gap-2 w-full mt-4"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : <><CheckCircle size={16} /> Submit OMR Answers</>}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
